import { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import {
  ChatBubbleOutline,
  Delete,
  Edit,
  MoreVert,
  PushPin,
  PushPinOutlined,
} from "@mui/icons-material";
import { formatDistanceToNow } from "date-fns";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../../app/hooks";
import {
  deleteWallPost,
  togglePinWallPost,
  toggleWallReaction,
} from "../wallSlices";
import { REACTION_TYPES } from "../wallConstants";
import { employeeLabel } from "../wallSearch";
import { getUploadBaseUrl } from "../../process/processSlices";
import { WallPost, WallUser } from "../types/wallTypes";
import CommentList from "./CommentList";
import VoiceNotePlayer from "./VoiceNotePlayer";
import AttachmentGrid from "./AttachmentGrid";

type Props = {
  post: WallPost;
  officeUsers: WallUser[];
  currentUserId?: string;
  canModerate: boolean;
  highlighted?: boolean;
  onEdit?: (post: WallPost) => void;
};

export default function WallPostCard({
  post,
  officeUsers,
  currentUserId,
  canModerate,
  highlighted,
  onEdit,
}: Props) {
  const { t } = useTranslation();
  const { t: te } = useTranslation("enums");
  const dispatch = useAppDispatch();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showComments, setShowComments] = useState(false);

  useEffect(() => {
    if (highlighted) setShowComments(true);
  }, [highlighted]);

  const isAuthor = post.author?._id === currentUserId;

  const reactionCount = (type: string) =>
    (post.reactions ?? []).filter((r) => r.type === type).length;
  const reactedByMe = (type: string) =>
    (post.reactions ?? []).some(
      (r) => r.type === type && r.user?._id === currentUserId,
    );

  return (
    <Card
      id={`wall-post-${post._id}`}
      sx={{
        mb: 2,
        ...(highlighted
          ? {
              outline: (theme) => `2px solid ${theme.palette.primary.main}`,
              boxShadow: (theme) => `0 0 0 4px ${theme.palette.primary.main}33`,
            }
          : {}),
      }}
    >
      <CardHeader
        avatar={
          <Avatar src={post.author?.avatar}>{post.author?.name?.[0]}</Avatar>
        }
        title={
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="subtitle2">{post.author?.name}</Typography>
            <Chip
              size="small"
              label={te(`wallCategory.${post.category}`)}
              color="primary"
              variant="outlined"
            />
            {post.pinned && <PushPin fontSize="small" color="warning" />}
          </Stack>
        }
        subheader={
          <>
            {post.createdAt &&
              formatDistanceToNow(new Date(post.createdAt), {
                addSuffix: true,
              })}
            {post.editedAt ? ` · ${t("edited")}` : ""}
          </>
        }
        action={
          (isAuthor || canModerate) && (
            <>
              <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
                <MoreVert />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
              >
                {isAuthor && onEdit && (
                  <MenuItem
                    onClick={() => {
                      setAnchorEl(null);
                      onEdit(post);
                    }}
                  >
                    <Edit fontSize="small" sx={{ mr: 1 }} />
                    {t("editPost")}
                  </MenuItem>
                )}
                {canModerate && (
                  <MenuItem
                    onClick={() => {
                      setAnchorEl(null);
                      dispatch(togglePinWallPost(post._id!));
                    }}
                  >
                    {post.pinned ? (
                      <PushPinOutlined fontSize="small" sx={{ mr: 1 }} />
                    ) : (
                      <PushPin fontSize="small" sx={{ mr: 1 }} />
                    )}
                    {post.pinned ? t("unpin") : t("pin")}
                  </MenuItem>
                )}
                <MenuItem
                  onClick={() => {
                    setAnchorEl(null);
                    dispatch(deleteWallPost(post._id!));
                  }}
                >
                  <Delete fontSize="small" sx={{ mr: 1 }} />
                  {t("deletePost")}
                </MenuItem>
              </Menu>
            </>
          )
        }
      />
      <CardContent sx={{ pt: 0 }}>
        {post.message && (
          <Typography variant="body1" sx={{ whiteSpace: "pre-wrap", mb: 1.5 }}>
            {post.message}
          </Typography>
        )}

        {post.audioUrl && (
          <Box sx={{ mb: 1.5 }}>
            <VoiceNotePlayer src={`${getUploadBaseUrl()}/${post.audioUrl}`} />
          </Box>
        )}

        {post.attachments && post.attachments.length > 0 && (
          <Box sx={{ mb: 1.5 }}>
            <AttachmentGrid attachments={post.attachments} />
          </Box>
        )}

        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 0.5 }}>
          {post.complaint && (
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              label={`${t("complaint")}: ${post.complaint.subject ?? ""}`}
              component={RouterLink}
              to={`/app/complaints/${post.complaint._id}`}
              clickable
            />
          )}
          {(post.employees ?? []).map((e) => (
            <Chip
              key={e._id}
              size="small"
              color="info"
              variant="outlined"
              label={employeeLabel(e)}
              component={RouterLink}
              to={`/app/employee-detail/${e._id}`}
              clickable
            />
          ))}
          {(post.brokers ?? []).map((b, i) => (
            <Chip
              key={`b-${i}`}
              size="small"
              color="secondary"
              variant="outlined"
              label={`${t("broker")}: ${b.company?.displayName ?? b.name}`}
            />
          ))}
          {(post.contacts ?? []).map((c, i) => (
            <Chip
              key={`c-${i}`}
              size="small"
              variant="outlined"
              label={`${t("contact")}: ${c.name}`}
            />
          ))}
          {(post.mentions ?? []).map((m) => (
            <Chip key={m._id} size="small" color="success" label={`@${m.name}`} />
          ))}
          {(post.tags ?? []).map((tag, i) => (
            <Chip key={`t-${i}`} size="small" label={`#${tag}`} />
          ))}
        </Stack>
      </CardContent>

      <Divider />
      <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", gap: 1 }}>
        {REACTION_TYPES.map((type) => (
          <Button
            key={type}
            size="small"
            variant={reactedByMe(type) ? "contained" : "text"}
            onClick={() =>
              dispatch(toggleWallReaction({ postId: post._id!, type }))
            }
            sx={{ minWidth: 0, px: 1 }}
          >
            {type} {reactionCount(type) || ""}
          </Button>
        ))}
        <Box sx={{ flex: 1 }} />
        <Button
          size="small"
          startIcon={<ChatBubbleOutline />}
          onClick={() => setShowComments((s) => !s)}
        >
          {post.comments?.length ?? 0} {t("comments")}
        </Button>
      </Box>

      {showComments && (
        <CardContent sx={{ pt: 0 }}>
          <CommentList
            post={post}
            officeUsers={officeUsers}
            currentUserId={currentUserId}
            canModerate={canModerate}
          />
        </CardContent>
      )}
    </Card>
  );
}
